import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import {
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { auth, db } from "../FirebaseConfig";

/**
 * Identity model (Paseo pattern):
 *   - Firebase Auth UID is the canonical user identity.
 *   - `Usuarios/{authUid}` Firestore doc carries role/permissions.
 *   - Auth state lives in browser IndexedDB; no server-side session cookie.
 *
 * AuthProvider exposes:
 *   - firebaseUser:  the raw Firebase Auth user (or null when signed out)
 *   - usuario:       the Usuarios doc (or null while still loading / no doc)
 *   - loading:       true until both firebaseUser and usuario have resolved
 *     once. Anything depending on isAdmin / roles should wait for !loading.
 *
 * Permissions on the Usuarios doc are plain fields, not custom claims —
 * an admin can flip them in the Usuarios screen and the change is live
 * immediately (no token refresh needed).
 */

export interface Usuario {
  /** Doc ID == Firebase Auth UID. */
  id: string;
  email: string | null;
  displayName: string | null;
  initials: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isDeleted: boolean;
  /** Role tags: "pilot", "requester", etc. Admin is its own boolean. */
  roles: string[];
  /** Default account code the user submits flights under (UI prefill). */
  defaultAccountCode: string | null;
  /** Accounts this user is allowed to bill. null / empty = unrestricted. */
  accountCodes: string[] | null;
}

interface AuthCtx {
  firebaseUser: User | null;
  usuario: Usuario | null;
  loading: boolean;
}

const AuthContext = createContext<AuthCtx>({
  firebaseUser: null,
  usuario: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [usuarioResolved, setUsuarioResolved] = useState(false);

  // Track the Firebase Auth state.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setFirebaseUser(u);
      setAuthResolved(true);
      if (!u) {
        // Signed out — clear the Usuarios doc subscription state too.
        setUsuario(null);
        setUsuarioResolved(true);
      } else {
        // Will be re-resolved by the Usuarios snapshot effect below.
        setUsuarioResolved(false);
      }
    });
    return () => unsub();
  }, []);

  // Live-subscribe to the user's Usuarios doc. The createUserDoc Cloud
  // Function auto-provisions this on first Firebase Auth user creation,
  // BUT only for genuinely new auth users. If the auth account predates
  // this Cloud Function (e.g., legacy app testing) the trigger never
  // fired — we backfill the doc client-side here with zero-privilege
  // defaults. Firestore rules allow self-create only when isAdmin /
  // isSuperAdmin / roles / isDeleted are all at their default values,
  // so this can't be abused to grant access.
  useEffect(() => {
    if (!firebaseUser) return;
    const ref = doc(db, "Usuarios", firebaseUser.uid);
    let backfillAttempted = false;
    let backfillTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let lastSignInWritten = false;

    async function attemptBackfill() {
      if (cancelled || backfillAttempted) return;
      backfillAttempted = true;
      try {
        // Transactional backfill: if `createUserDoc` already created
        // the doc (with merged legacy perms), the transaction sees it
        // exists and exits without writing. Prevents the client from
        // clobbering a freshly-merged Usuarios doc when the snapshot
        // and the cloud trigger race on a first sign-in.
        await runTransaction(db, async (tx) => {
          if (cancelled) return;
          const cur = await tx.get(ref);
          if (cur.exists()) return;
          tx.set(ref, {
            email: firebaseUser!.email ?? "",
            displayName: firebaseUser!.displayName ?? "",
            initials: deriveInitials(
              firebaseUser!.displayName,
              firebaseUser!.email
            ),
            isAdmin: false,
            isSuperAdmin: false,
            isDeleted: false,
            roles: [],
            defaultAccountCode: null,
            authUid: firebaseUser!.uid,
            createdAt: serverTimestamp(),
          });
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[auth] Usuarios backfill failed:", err);
        setUsuario(null);
        setUsuarioResolved(true);
      }
    }

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          // Doc appeared (either pre-existing, created by the cloud
          // trigger, or just written by our backfill) — cancel any
          // pending backfill timer so we don't double-write.
          if (backfillTimer) {
            clearTimeout(backfillTimer);
            backfillTimer = null;
          }
          const d = snap.data();
          setUsuario({
            id: snap.id,
            email: d.email ?? firebaseUser.email,
            displayName: d.displayName ?? firebaseUser.displayName,
            initials: d.initials ?? null,
            isAdmin: d.isAdmin === true,
            isSuperAdmin: d.isSuperAdmin === true,
            isDeleted: d.isDeleted === true,
            roles: Array.isArray(d.roles) ? d.roles : [],
            defaultAccountCode: d.defaultAccountCode ?? null,
            accountCodes: Array.isArray(d.accountCodes)
              ? d.accountCodes
              : null,
          });
          setUsuarioResolved(true);
          // Stamp the last-sign-in on first snapshot of the session.
          if (!lastSignInWritten) {
            lastSignInWritten = true;
            updateDoc(ref, { lastSignInAt: serverTimestamp() }).catch(
              (err) => {
                // eslint-disable-next-line no-console
                console.warn("[auth] lastSignInAt update failed:", err);
              }
            );
          }
          return;
        }
        // Doc missing. The `createUserDoc` cloud trigger usually fires
        // within 1-3s of a brand-new Google sign-in and may merge a
        // pre-existing pre-login Usuarios doc by email. If we backfill
        // immediately we either (a) show "Sin acceso" before the
        // trigger has a chance to merge, or (b) overwrite the merged
        // doc with zero perms. Wait briefly; if the trigger finishes
        // first the snapshot will refire with exists=true and we
        // cancel the timer. Otherwise we backfill via a transaction
        // that no-ops if the doc has appeared in the meantime.
        if (!backfillAttempted && backfillTimer == null) {
          backfillTimer = setTimeout(() => {
            backfillTimer = null;
            void attemptBackfill();
          }, 3000);
        }
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("[auth] Usuarios snapshot failed:", err);
        setUsuarioResolved(true);
      }
    );
    return () => {
      cancelled = true;
      if (backfillTimer) clearTimeout(backfillTimer);
      unsub();
    };
  }, [firebaseUser]);

  const value = useMemo<AuthCtx>(
    () => ({
      firebaseUser,
      usuario,
      loading: !authResolved || (!!firebaseUser && !usuarioResolved),
    }),
    [firebaseUser, usuario, authResolved, usuarioResolved]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthCtx {
  return useContext(AuthContext);
}

/**
 * App-level access gate. A signed-in Google account can only USE the
 * app when:
 *   - not soft-deleted
 *   - AND has the 'user' role tag in their Usuarios doc, OR is admin /
 *     super admin (which implicitly grants login access)
 *
 * Pilots that exist only to receive assignment emails do NOT get the
 * 'user' role and so are blocked from the app even if they sign in
 * with Google. Admins assign the 'user' tag from /admin/usuarios.
 */
export function hasAccess(u: Usuario | null): boolean {
  if (!u || u.isDeleted) return false;
  return u.isAdmin || u.isSuperAdmin || u.roles.includes("user");
}

// "Camilo Justiniano" -> "CJ"; "cjb@albinversiones.cl" -> "CJ"
function deriveInitials(displayName: string | null, email: string | null): string {
  const src = displayName || email || "";
  if (!src) return "";
  const parts = src.replace(/@.*$/, "").split(/[\s.\-_]+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]!).toUpperCase();
}
